import type {
  NodeKey,
  ProvenanceCapture,
  SlotInputNode,
  SlotValuationInputsResponse,
} from 'types/packages-api/slot-valuation-inputs';
import {
  blockCaptionsForSection,
  EXPENSE_CLASS_CAPTIONS,
  EXPENSE_CLASS_LABELS,
  renderedRowNodes,
  NOT_USED_MARKER_LABEL,
  notUsedRowsForSection,
  ROW_NOTE_MARKER_LABEL,
  sectionMakesNoClaim,
} from './source-blocks';
import {
  type RowGateContext,
  rowGateContextFor,
  rowNotesForSection,
} from './row-gate-utils';
import { SeriesDisclosure } from './series-disclosure';
import { TearsheetLink } from './tearsheet-link';
import { PdpDeclineHeader, TypeCurveHeader } from './tearsheet-block-headers';
import { DatesTable } from './tearsheet-dates';
import {
  EditAffordance,
  KindMarker,
  NodeValue,
  OverrideMarker,
  RowAsideMarker,
  ValueTable,
} from './tearsheet-cells';
import { rowCanCarryOverride } from './override-badge';
import { type MetaByNode, type ZoneSection } from './tearsheet-sections';
import {
  controlForNode,
  EDIT_AFFORDANCE_LABEL,
  EDIT_AFFORDANCE_TITLE,
  isNodeLocked,
} from './editability';
import {
  absentProvenanceNote,
  DOMAIN_CONTEXT_NOUN,
  type OperationField,
  type SeriesExtent,
} from './utils';
import styles from './slot-tearsheet.module.scss';

/**
 * One zone section — its title, the scenario / area context, the formula lines, and
 * a Parameter/Value table of its nodes.
 *
 * @param {object} props - The zone section to render.
 * @return {JSX.Element} The section.
 */
export function ZoneSectionView({
  section,
  meta,
  slotCategory,
  dates,
  allNodes,
  capture,
  extent,
  response,
  disclosed,
  onEdit,
  onRerun,
  operations,
  onRevert,
}: {
  section: ZoneSection;
  meta: MetaByNode;
  slotCategory: string | null;
  dates: SlotValuationInputsResponse['dates'] | undefined;
  allNodes: SlotInputNode[];
  capture: ProvenanceCapture | null;
  extent: SeriesExtent;
  response: SlotValuationInputsResponse;
  /** Series columns rendered in some disclosure on this page. See disclosedSeriesKeys. */
  disclosed: Set<NodeKey>;
  /** Opens the edit modal for a row. Absent when this surface owns no override log. */
  onEdit?: (node: SlotInputNode, rowLabel: string) => void;
  /** The route out of the pending state. See OverrideMarker. */
  onRerun?: () => void;
  /** The operation grouping, built once for the payload. See OverrideMarker. */
  operations?: Map<string, OperationField[]>;
  /** The revert. See OverrideMarker. */
  onRevert?: (node: SlotInputNode) => void;
}) {
  const isSlotInputs = section.zone === 'cashflow';
  // MUST AGREE WITH THE CONDITIONS BELOW. A run that recorded a price_deck_id but no
  // price_deck_name has an href and no name; gating the whole header on the NAME would hide
  // the link that the row beneath is ready to draw.
  const hasContext = Boolean(
    section.scenario || section.scenarioHref || section.area
  );
  // The same test, shared with the workbook — see sectionMakesNoClaim. `hasContext` stays as
  // it is because the header conditions below read it on its own, without the domain half.
  const makesNoClaim = sectionMakesNoClaim(section);
  // ── A ROW SCOPED TO ANOTHER RESERVE CATEGORY IS NOT AN INPUT TO THIS SLOT ──────────
  //
  // The ask: "why show LTD discount rate on a PDP well." The page's own heading is "Inputs used
  // to value slot X", and a rate belonging to a different reserve category was never a fact
  // about this slot — it is the scenario's configuration for other wells. Showing it was the
  // error; FADING it to 0.45 was a half-measure that conceded the row was clutter while
  // keeping its height, which is the complaint.
  //
  // Measured: this removes 12 rows per slot in every category — 9 non-matching discount
  // rates that DO carry a value, plus 3 non-matching schedule functions that never do — and
  // keeps the 4 belonging to this slot's own category.
  //
  // THE SAME PREDICATE THAT DREW THE FADE, not a new one. isUnappliedCategoryRate fails
  // open: no node category or no slot category means "do not remove", so a slot whose
  // category did not resolve keeps every row rather than silently losing twelve.
  // BOTH RULES NOW LIVE IN renderedRowNodes, which the Excel export calls too — so the
  // workbook's rows are the same rows by construction rather than by a second copy of this
  // filter agreeing with it.
  const rowsFor = (nodes: SlotInputNode[]) =>
    renderedRowNodes(
      nodes,
      disclosed,
      (key) => meta[key]?.category,
      slotCategory,
      (key) => meta[key]?.source
    );
  // Only asked for a domain with nothing to show — see absentProvenanceNote.
  // ── A ZONE WITH NO DOMAIN HAS NO SCENARIO TO REPORT ──────────────────────────────────
  //
  // absentProvenanceNote's final fallback (utils.ts:1162) reads "No scenario recorded for
  // this domain", and it is reached whenever `provenance` is null. That is true for every
  // zone whose ModelMap::ZONE_DOMAINS entry is nil — PDP Decline, Type Curve, Volumes and
  // Cashflow — so all four asserted a MISSING SCENARIO for a block that has no scenario
  // concept at all. Measured 22 Sep 2026: the live payload's `domains` array carries exactly
  // the seven real domains, and none of those four is among them.
  //
  // THE GUARD IS HERE, NOT INSIDE THE NOTE. The note answers "what should a blank domain
  // say"; whether this zone HAS a domain is the caller's fact, and `section.domain` already
  // carries it. Pushing the test inside would make the function answer a question it cannot
  // see the inputs for — the same shape as the `hasParameterRows` helper that was deleted
  // for answering narrower than its caller needed.
  //
  // The seven real domains are untouched, INCLUDING a genuine "no scenario recorded": that
  // case has a domain, so `section.domain` is present and the note is still asked.
  const absent = makesNoClaim
    ? null
    : absentProvenanceNote(section.provenance, capture);
  // ── WHICH ROWS THIS RUN DID NOT USE ────────────────────────────────────────────────────
  //
  // The AFE rows the run ignored and the rows a captured-but-unscheduled area supplied, each
  // with its own sentence. ONE GATE, SHARED WITH THE WORKBOOK — see notUsedRowsForSection,
  // which also carries the caption-suppression rule the area rows inherit.
  // INS-13307: the run's own volume route, split-well options and sources, the rows its calculator
  // could not price, and the registry's sparse per-node notes — one builder, shared with the workbook.
  const gateContext: RowGateContext = rowGateContextFor(response, meta);
  const notUsed = notUsedRowsForSection(
    section,
    allNodes,
    (key) => meta[key]?.source,
    gateContext
  );
  const rowNotes = rowNotesForSection(allNodes, gateContext, notUsed);
  // ONE ROW RENDERER for the expense groups and the flat table, so a control added to one
  // cannot be missing from the other.
  const renderRow = (node: SlotInputNode) => (
    <tr key={node.key}>
      <td className={styles.paramName}>
        {node.label}
        <KindMarker kind={node.kind} />
        {/* THE FORMULA IS NOT HERE ANY MORE — it renders in the VALUE cell, where for a
            derivation row it is the content rather than a caption. See NodeValue. `.formula`
            is still used by the Milestones table's schedule formula (AC 10) and by the
            Calculated result, both of which sit beside rows that DO carry values. */}
      </td>
      <td className={styles.paramValue}>
        <div className={styles.valueCellRow}>
          <div className={styles.valueCellMain}>
            <NodeValue
              node={node}
              unit={meta[node.key]?.unit}
              source={meta[node.key]?.source}
              extent={extent}
            />
            <OverrideMarker
              node={node}
              unit={meta[node.key]?.unit}
              meta={meta[node.key]}
              onRerun={onRerun}
              operations={operations}
              onRevert={onRevert}
            />
          </div>
          {notUsed.has(node.key) && (
            <RowAsideMarker
              announced={node.label}
              text={notUsed.get(node.key) ?? ''}
              label={NOT_USED_MARKER_LABEL}
              className={styles.rowNotUsed}
            />
          )}
          {rowNotes.has(node.key) && (
            <RowAsideMarker
              announced={node.label}
              text={rowNotes.get(node.key) ?? ''}
              label={ROW_NOTE_MARKER_LABEL}
              className={styles.rowNote}
            />
          )}
          <EditAffordance
            node={node}
            editable={
              section.editableKeys.has(node.key) &&
              // MOVED, NOT WITHDRAWN. The three scheduled dates are edited from their
              // Milestones row, which is the row that shows the result — see
              // MilestoneEditAffordance. The registry still marks them editable; this is only
              // about which of the two rows carries the button.
              rowCanCarryOverride(meta[node.key])
            }
            locked={isNodeLocked(meta[node.key], dates)}
            control={controlForNode(meta[node.key])}
            onEdit={onEdit}
          />
        </div>
      </td>
    </tr>
  );
  const flatRows = section.groups.length === 0 ? rowsFor(section.nodes) : [];
  // The volume source caption, the pending Volume Source note and the block notes, in order — one
  // function, which the workbook calls too.
  const captions = blockCaptionsForSection(
    section,
    rowsFor(section.nodes),
    allNodes,
    {
      response,
      sourceFor: (key) => meta[key]?.source,
      unitFor: (key) => meta[key]?.unit,
    }
  );
  return (
    <section className={styles.zoneSection}>
      <div className={styles.zoneHeader}>
        <h4 className={styles.zoneTitle}>{section.label}</h4>
        {/* ── THE EDIT AFFORDANCE ─────────────────────────────────────────────────────
            PERSISTENT, not hover-only: it must survive a screenshot and be reachable
            without a pointer, so it is real text and not a tooltip.

            ⚠️ THIS USED TO DESCRIBE A `title` AND A `tabIndex={0}`,
            and the markup below has neither. A `title` on a
            non-interactive element is announced inconsistently and putting a static label
            in the tab order gives a keyboard user a stop that does nothing. The explanation
            is a visually hidden sibling referenced by `aria-describedby`, which a screen
            reader announces with the label and a pointer user never sees.

            The RULE lives in editability.ts and nowhere else: `buildSections` runs
            `isNodeEditable` per node into `section.editableKeys`, and this is a render of
            whether that set is empty. (It used to name an `anyEditable` helper that was
            deleted, so the comment named nothing.) The design is unconfirmed;
            moving to a per-row marker is a change to that module and
            this block, not a sweep. */}
        {section.editableKeys.size > 0 && (
          <>
            <span
              className={styles.editableMarker}
              aria-describedby={`${section.zone}-editable-hint`}
            >
              {EDIT_AFFORDANCE_LABEL}
            </span>
            <span
              id={`${section.zone}-editable-hint`}
              className={styles.editableMarkerHint}
            >
              {EDIT_AFFORDANCE_TITLE}
            </span>
          </>
        )}
        {hasContext && (
          <span className={styles.zoneContext}>
            {/* `section.domain &&` IS LOAD-BEARING, not defensive noise. The three
                zones with no domain (Type Curve, Volumes, Cashflow) carry
                `domain: null`, and DOMAIN_CONTEXT_NOUN is keyed by DomainKey — so
                indexing it below needs the null ruled out. It is unreachable at
                runtime, because `scenario` and `scenarioHref` both come from the
                domain group and are null when there is no domain; tsc cannot see
                that, and this is the guard that makes the type and the reasoning
                agree instead of asserting past it. */}
            {section.domain && (section.scenario || section.scenarioHref) && (
              <span>
                {/* NAMED FROM THE DOMAIN, not hardcoded "Scenario" — a price deck is not a
                    scenario, and it is the fifth domain to carry one of these. */}
                <span className={styles.contextLabel}>
                  {DOMAIN_CONTEXT_NOUN[section.domain]}:{' '}
                </span>
                {/* A LINK ONLY WHEN THE SERVER SENT A PATH. `scenarioHref` is null for
                    royalty and expenses — no such class exists — and null whenever the
                    slice carried no id. Building a path here from the domain or the name is
                    how a dead link ships.
                    THE NAME IS OPTIONAL, THE PATH IS NOT. A run that recorded a
                    price_deck_id but no price_deck_name links on the id rather than
                    inventing a name for it. */}
                {section.scenarioHref ? (
                  <TearsheetLink href={section.scenarioHref}>
                    {section.scenario ?? section.scenarioId ?? 'View'}
                  </TearsheetLink>
                ) : (
                  <strong>{section.scenario}</strong>
                )}
              </span>
            )}
            {section.area && (
              <span>
                <span className={styles.contextLabel}>Area: </span>
                <strong>{section.area}</strong>
              </span>
            )}
          </span>
        )}
        {/* A CAPTION, not a banner. The page is not broken — it simply cannot explain
            this domain, and saying which is the whole of spec 5.4. */}
        {absent && (
          <span
            className={
              absent.kind === 'unknown'
                ? styles.provenanceUnknown
                : styles.provenanceClaim
            }
          >
            {absent.text}
          </span>
        )}
      </div>
      {/* WHAT THE VOLUMES CAME FROM, stated rather than implied by five blank rows. A PDP
          slot carries a type-curve id whose parameters do not exist — measured: zero of
          type_curve_parameters' 5,516 rows are PDP/IHS ids — so blank IS the right answer
          and the header says so instead of looking like missing data. */}
      {/* THE CURVE LEADS, then the sentence about where the volumes came from. On a slot
          with no curve the header renders nothing and the caption stands alone. */}
      {/* ONE BLOCK, ONE CAPTION, TWO POSSIBLE HEADERS. The header says WHICH source (the
          named curve, or the named decline linked to the well page, which was asked for
          one specifically); the caption says WHAT KIND. They cannot both render: a slot
          carries a type curve or a decline, never both, measured at 0 of 686 slots. */}
      <TypeCurveHeader
        volumeSource={section.volumeSource}
        allNodes={allNodes}
      />
      <PdpDeclineHeader pdpDecline={section.volumeSource} allNodes={allNodes} />
      {/* The kind's caption, then (volumes only) the pending Volume Source change: the Volume
          Source row shows the new id with its own pending marker, and the parameters below are
          the ones this run resolved. Then the block notes. blockCaptionsForSection. */}
      {captions.map((note) => (
        <p key={note} className={styles.blockCaption}>
          {note}
        </p>
      ))}
      {section.zone === 'development_pace' && (
        <DatesTable
          dates={dates}
          nodes={allNodes}
          meta={meta}
          onRerun={onRerun}
          operations={operations}
          onRevert={onRevert}
          editableKeys={section.editableKeys}
          onEdit={onEdit}
        />
      )}
      {/* CAPITAL AND OPERATING COSTS ARE DIFFERENT QUESTIONS and get their own tables.
          Membership is derived in classifyExpenses from the registry's own edges — no node
          key is listed here. A group with no rows is not rendered. */}
      {section.groups.length > 0 &&
        section.groups.map((group) => {
          const groupRows = rowsFor(group.nodes);
          return (
            <div key={group.expenseClass} className={styles.expenseGroup}>
              <h5 className={styles.expenseGroupTitle}>
                {EXPENSE_CLASS_LABELS[group.expenseClass]}
              </h5>
              {EXPENSE_CLASS_CAPTIONS[group.expenseClass] && (
                <p className={styles.blockCaption}>
                  {EXPENSE_CLASS_CAPTIONS[group.expenseClass]}
                </p>
              )}
              {groupRows.length > 0 && (
                <ValueTable head="Parameter">
                  {groupRows.map(renderRow)}
                </ValueTable>
              )}
            </div>
          );
        })}
      {section.groups.length === 0 && flatRows.length > 0 && (
        <ValueTable head={isSlotInputs ? 'Input' : 'Parameter'}>
          {/* TWO RULES DECIDE WHAT RENDERS HERE, and both are derived: a disclosed monthly
              node gets no row (parameterRowNodes), and neither does a row scoped to another
              reserve category (rowsFor). */}
          {flatRows.map(renderRow)}
        </ValueTable>
      )}
      {/* MONTHS DOWN, STREAMS ACROSS. Which columns land here is derived from each column
          node's own declared zone — see seriesColumnsForZone. A block with no monthly
          columns renders no disclosure at all. */}
      <SeriesDisclosure
        columns={section.seriesColumns}
        response={response}
        unitFor={(key) => meta[key]?.unit}
        lengthFor={(key) =>
          allNodes.find((node) => node.seriesKey === key)?.seriesLength
        }
        labelFor={(key) =>
          meta[key]?.label ??
          allNodes.find((node) => node.key === key)?.label ??
          key
        }
        /* FROM THE FIRST RESPONSE, like the output rows' formulas — `slotInputs` carries
           `formula` on every node, so the three realized-price formulas paint without
           waiting on the model map and cannot disagree with a row that shows the same
           node. The model map is NOT consulted here for that reason. */
        formulaFor={(key) =>
          allNodes.find((node) => node.key === key)?.formula ?? null
        }
      />
    </section>
  );
}
